// Login Page
import { APP_VERSION } from '../lib/version';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Truck, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { authApi, setupApi, publicApi } from '../lib/api';
import { useAuthStore, useWacheStore } from '../lib/store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const loginSchema = z.object({
  loginname: z.string().min(1, 'Loginname erforderlich'),
  passwort: z.string().min(1, 'Passwort erforderlich'),
});

export function LoginPage() {
  const navigate = useNavigate();
  const { setUser, isAuthenticated } = useAuthStore();
  const { setWache } = useWacheStore();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [wacheConfig, setWacheConfig] = useState(null);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { loginname: '', passwort: '' },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    // Check setup status
    const checkSetup = async () => {
      try {
        const [statusRes, configRes] = await Promise.all([
          setupApi.getStatus(),
          publicApi.getWacheConfig().catch(() => null),
        ]);
        
        setNeedsSetup(statusRes.data.needs_setup);
        if (configRes?.data) {
          setWacheConfig(configRes.data);
        }
      } catch (error) {
        console.error('Setup check failed:', error);
      }
    };

    checkSetup();
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await authApi.login(data);
      const { user } = response.data;
      
      setUser(user);
      
      // Fetch wache config
      try {
        const wacheRes = await publicApi.getWacheConfig(user.wache_id);
        setWache(wacheRes.data);
      } catch (e) {
        console.error('Failed to load wache config:', e);
      }

      toast.success('Erfolgreich angemeldet');
      
      // Redirect based on password change requirement
      if (user.muss_passwort_aendern) {
        toast.warning('Bitte ändern Sie Ihr Passwort', { 
          description: 'Sie müssen Ihr temporäres Passwort ändern.',
          duration: 5000 
        });
        navigate('/profil?change_password=true');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Anmeldung fehlgeschlagen';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  if (needsSetup) {
    navigate('/setup');
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        {/* Logo and Title */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-20 h-20">
            <img
              src={wacheConfig?.logo_url || '/logo-fwonline.svg'}
              alt="Wache-Logo"
              className="w-full h-full object-contain"
              onError={(e) => { e.target.src = '/logo-fwonline.svg'; }}
            />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            {wacheConfig?.name
              ? (/^feuerwache/i.test(wacheConfig.name) ? wacheConfig.name : `Feuerwache ${wacheConfig.name}`)
              : 'Digitale Feuerwache'}
          </h1>
          {wacheConfig?.nummer && (
            <p className="text-muted-foreground">FW {wacheConfig.nummer}</p>
          )}
        </div>

        {/* Login Card */}
        <Card className="shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-heading">Anmelden</CardTitle>
            <CardDescription>
              Geben Sie Ihre Zugangsdaten ein
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="loginname">Loginname</Label>
                <Input
                  id="loginname"
                  type="text"
                  placeholder="Ihr Loginname"
                  autoComplete="username"
                  data-testid="login-username"
                  {...register('loginname')}
                />
                {errors.loginname && (
                  <p className="text-sm text-destructive">{errors.loginname.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwort">Passwort</Label>
                <div className="relative">
                  <Input
                    id="passwort"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Ihr Passwort"
                    autoComplete="current-password"
                    data-testid="login-password"
                    {...register('passwort')}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {errors.passwort && (
                  <p className="text-sm text-destructive">{errors.passwort.message}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full uppercase tracking-wide font-semibold"
                disabled={isLoading}
                data-testid="login-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  'Anmelden'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer with Version */}
        <p className="text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Creativey Media · Version {APP_VERSION}
        </p>
        
        {/* Developer Access Link */}
        <p className="text-center">
          <Link 
            to="/developer-login" 
            className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          >
            Developer-Zugang
          </Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
